import { ComponentType, useEffect } from "react";

import { AppProps } from "next/app";
import Head from "next/head";

import { LanguageProvider } from "@/contexts/LanguageContext";
import ThemeProvider from "@/contexts/ThemeContext";
import useIsReactNative from "@/hooks/useIsReactNative";
import useTheme from "@/hooks/useTheme";
import ICategory from "@/interfaces/ICategory";
import ICompany from "@/interfaces/ICompany";
import ISession from "@/interfaces/ISession";
import GlobalStyle from "@/styles/global";
import SkeletonGlobalStyle from "@/styles/skeleton";
import DisableContextMenu from "@/utils/DisableContextMenu";
import { PreventInstallPrompt } from "@/utils/InstallPrompt";

import "@/styles/fonts.css";

type AppPropsWithPageLayout = AppProps<{
  session?: ISession;
  company?: ICompany;
  categories?: ICategory[];
  primaryColor?: string;
  language?: string;
}> & {
  Component: AppProps["Component"] & {
    PageLayout?: ComponentType<any>;
  };
};

function MyAppHeader() {
  const { theme } = useTheme();
  const isReactNative = useIsReactNative();

  return (
    <Head>
      <meta name="color-scheme" content={theme.title} />
      <meta name="theme-color" content={theme.background} />
      <meta
        name="viewport"
        content={
          isReactNative
            ? "minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, user-scalable=no, viewport-fit=cover"
            : "width=device-width, initial-scale=1.0"
        }
      />
    </Head>
  );
}

export default function MyApp({
  Component,
  pageProps: { primaryColor, language, ...pageProps },
}: AppPropsWithPageLayout) {
  useEffect(() => {
    DisableContextMenu();
    PreventInstallPrompt();
  }, []);

  return (
    <ThemeProvider primary={primaryColor}>
      <LanguageProvider language={language}>
        <GlobalStyle />
        <SkeletonGlobalStyle />
        <MyAppHeader />
        {Component.PageLayout ? (
          <Component.PageLayout {...pageProps}>
            <Component {...pageProps} />
          </Component.PageLayout>
        ) : (
          <Component {...pageProps} />
        )}
      </LanguageProvider>
    </ThemeProvider>
  );
}
